import { APIExtRoutes } from "@constants";
import { APIResponse } from "@models/core";
import {
  ModelScreenerData,
  ModelScreenerExportModelsDto,
  MSExcelDownloadDto,
} from "@models/model-screener/entities/modelScreenerData";
import {
  ModelScreenerColumnSettingDTO,
  ModelScreenerFilterDTO,
} from "@models/model-screener/entities/modelScreenerFilter";
import { APIGatewayService, handleErrorProxy } from "@services/shService";
import { generatePath } from "react-router";

export const getModelScreenerData = async ({
  signal,
  filter,
  columnSetting,
}: {
  signal?: AbortSignal;
  filter: ModelScreenerFilterDTO | null;
  columnSetting: ModelScreenerColumnSettingDTO | null;
}): Promise<APIResponse<ModelScreenerData>> => {
  const response = await APIGatewayService.post<ModelScreenerData>(
    APIExtRoutes.modelScreeners,
    {
      filter,
      columnSetting,
    },
    { signal: signal },
  );

  return handleErrorProxy(response);
};

export const getModelScreenerDataByDateRange = async ({
  signal,
  fromDate,
  toDate,
  filter,
  columnSetting,
}: {
  signal?: AbortSignal;
  fromDate: Date | null;
  toDate: Date | null;
  filter: ModelScreenerFilterDTO | null;
  columnSetting: ModelScreenerColumnSettingDTO | null;
}): Promise<APIResponse<ModelScreenerData>> => {
  let response = await APIGatewayService.post<ModelScreenerData>(
    generatePath(APIExtRoutes.modelScreenersDateRange, {
      startDate: fromDate ? fromDate.toISOString() : "",
      endDate: toDate ? toDate.toISOString() : "",
    }),
    {
      filter,
      columnSetting,
    },
    {
      signal: signal,
    },
  );

  return handleErrorProxy(response);
};

export const putModelScreenerFavoriteModels = async (
  modelIds: string[],
): Promise<APIResponse<string>> => {
  const response = await APIGatewayService.put<string>(
    APIExtRoutes.modelScreenersFavorited,
    modelIds,
  );

  return handleErrorProxy(response);
};

export const exportModelScreenerExcel = async (
  exportDto: ModelScreenerExportModelsDto,
): Promise<APIResponse<MSExcelDownloadDto>> => {
  const response = await APIGatewayService.post<MSExcelDownloadDto>(
    APIExtRoutes.modelScreenerExport,
    exportDto,
  );

  return handleErrorProxy(response);
};

export const getModelScreenerFilterSettings = async (): Promise<
  APIResponse<ModelScreenerFilterDTO[]>
> => {
  const response = await APIGatewayService.get<ModelScreenerFilterDTO[]>(
    APIExtRoutes.modelScreenerFilters,
  );

  return handleErrorProxy(response);
};

export const getModelScreenerFilterSettingById = async ({
  signal,
  filterSettingId,
  baseFilter,
}: {
  signal?: AbortSignal;
  filterSettingId: string;
  baseFilter?: ModelScreenerFilterDTO;
}): Promise<APIResponse<ModelScreenerFilterDTO>> => {
  const response = await APIGatewayService.post<ModelScreenerFilterDTO>(
    generatePath(APIExtRoutes.modelScreenerFilter, {
      filterId: filterSettingId,
    }),
    baseFilter,
    { signal: signal },
  );

  return handleErrorProxy(response);
};

export const deleteModelScreenerFilterSetting = async (
  filterSettingId: string,
): Promise<APIResponse<string>> => {
  const response = await APIGatewayService.delete<string>(
    generatePath(APIExtRoutes.modelScreenerFilter, {
      filterId: filterSettingId,
    }),
  );

  return handleErrorProxy(response);
};

export const setFilterSettingAsDefault = async (filterSettingId: string) => {
  const response = await APIGatewayService.patch(
    generatePath(APIExtRoutes.modelScreenerFilter, {
      filterId: filterSettingId,
    }),
  );

  return handleErrorProxy(response, response.data);
};

export const createModelScreenerFilterSetting = async (
  filterSetting: ModelScreenerFilterDTO,
  signal?: AbortSignal,
): Promise<APIResponse<ModelScreenerFilterDTO[]>> => {
  const response = await APIGatewayService.post<ModelScreenerFilterDTO[]>(
    APIExtRoutes.modelScreenerFilters,
    filterSetting,
    { signal: signal },
  );
  return handleErrorProxy(response, "Filter has been created.");
};

export const updateModelScreenerFilterSetting = async (
  filterSetting: ModelScreenerFilterDTO,
  signal?: AbortSignal,
): Promise<APIResponse<ModelScreenerFilterDTO[]>> => {
  const response = await APIGatewayService.put<ModelScreenerFilterDTO[]>(
    APIExtRoutes.modelScreenerFilters,
    filterSetting,
    { signal: signal },
  );

  return handleErrorProxy(response, "Changes have been saved.");
};

export const getModelScreenerColumnSettings = async (): Promise<
  APIResponse<ModelScreenerColumnSettingDTO[]>
> => {
  const response = await APIGatewayService.get<ModelScreenerFilterDTO[]>(
    APIExtRoutes.modelScreenerColumns,
  );

  return handleErrorProxy(response);
};

export const setMSColumnSettingAsDefault = async (columnSettingId: string) => {
  const response = await APIGatewayService.patch(
    generatePath(APIExtRoutes.modelScreenerColumn, {
      columnSettingId: columnSettingId,
    }),
  );

  return handleErrorProxy(response, response.data);
};

export const createMSColumnSetting = async (
  columnSetting: ModelScreenerColumnSettingDTO,
  signal?: AbortSignal,
): Promise<APIResponse<ModelScreenerColumnSettingDTO[]>> => {
  const response = await APIGatewayService.post<
    ModelScreenerColumnSettingDTO[]
  >(APIExtRoutes.modelScreenerColumns, columnSetting, { signal: signal });
  return handleErrorProxy(response, "View has been created.");
};

export const updateMSColumnSetting = async (
  columnSetting: ModelScreenerColumnSettingDTO,
  signal?: AbortSignal,
): Promise<APIResponse<ModelScreenerColumnSettingDTO[]>> => {
  const response = await APIGatewayService.put<ModelScreenerColumnSettingDTO[]>(
    APIExtRoutes.modelScreenerColumns,
    columnSetting,
    { signal: signal },
  );

  return handleErrorProxy(response, "Changes have been saved.");
};

export const getMSColumnSettingById = async ({
  signal,
  columnSettingId,
}: {
  signal?: AbortSignal;
  columnSettingId: string;
}): Promise<APIResponse<ModelScreenerColumnSettingDTO>> => {
  const response = await APIGatewayService.get<ModelScreenerColumnSettingDTO>(
    generatePath(APIExtRoutes.modelScreenerColumn, {
      columnSettingId: columnSettingId,
    }),
    { signal: signal },
  );

  return handleErrorProxy(response);
};

export const deleteModelScreenerFilterColumn = async (
  columnSettingId: string,
): Promise<APIResponse<string>> => {
  const response = await APIGatewayService.delete<string>(
    generatePath(APIExtRoutes.modelScreenerColumn, {
      columnSettingId: columnSettingId,
    }),
  );

  return handleErrorProxy(response, response.data);
};
